/**
 * Retired-system archive — Cluster A.
 *
 * The application is gone; the ability to answer questions about its data is
 * not. That is what separates an archive from a backup, and it is the whole
 * commercial argument for application retirement.
 */

import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Collapse,
  Group,
  Loader,
  Modal,
  Pagination,
  Paper,
  ScrollArea,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import {
  IconChevronDown,
  IconDatabaseExport,
  IconFileCertificate,
  IconFilterOff,
  IconBinaryTree2,
  IconLink,
  IconPlugConnectedX,
  IconSearch,
  IconTable,
  IconTrash,
} from "@tabler/icons-react";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { useAccessToken } from "@/features/auth/useAccessToken";
import {
  exportArchiveDataset,
  getArchiveContainer,
  getArchiveDatasetManifest,
  getArchiveDatasetRows,
  getArchiveDatasetSchema,
  getRecordViewRows,
  deleteRecordView,
  listArchiveDatasets,
  listRecordViews,
  type ArchiveContainer,
  type ArchiveDataset,
  type ArchiveDatasetSchema,
  type ArchiveJoinedRows,
  type ArchiveRecordView,
  type ArchiveRowsOut,
  type RowFilter,
} from "@/shared/api/archive";
import { useMe } from "@/features/users/useMe";
import { ArchiveJoinBuilder } from "./ArchiveJoinBuilder";
import { formatBytes, formatCount, formatDate } from "./format";

const OPS = [
  { value: "eq", label: "equals" },
  { value: "contains", label: "contains" },
  { value: "gt", label: "greater than" },
  { value: "lt", label: "less than" },
];

export function ArchiveDatasetsPage() {
  const { containerId = "" } = useParams();
  const getToken = useAccessToken();

  const [container, setContainer] = useState<ArchiveContainer | null>(null);
  const [datasets, setDatasets] = useState<ArchiveDataset[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [schema, setSchema] = useState<ArchiveDatasetSchema | null>(null);
  const [rows, setRows] = useState<ArchiveRowsOut | null>(null);
  const [filters, setFilters] = useState<RowFilter[]>([]);
  const [draft, setDraft] = useState<RowFilter>({ column: "", op: "eq", value: "" });
  const [freeText, setFreeText] = useState("");
  const [page, setPage] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [schemaOpen, { toggle: toggleSchema }] = useDisclosure(true);
  const [manifestOpen, { open: openManifest, close: closeManifest }] = useDisclosure(false);
  const [manifest, setManifest] = useState<Record<string, unknown> | null>(null);
  const [joinOpen, { open: openJoin, close: closeJoin }] = useDisclosure(false);

  // A saved record view is a *different selection* from a table, not a filter
  // on one: it produces one row per record across several tables. Selecting a
  // view therefore clears the active table, and vice versa — showing both at
  // once would leave the reader unsure which grid they are looking at.
  const [views, setViews] = useState<ArchiveRecordView[]>([]);
  const [activeViewId, setActiveViewId] = useState<string | null>(null);
  const [viewRows, setViewRows] = useState<ArchiveJoinedRows | null>(null);

  const { data: me } = useMe();
  // Anyone with access may explore a join; saving one publishes it to everyone
  // with access to the archive, which is an administrator's call.
  const canSaveView = (me?.roles ?? []).includes("administrator");

  useEffect(() => {
    void (async () => {
      try {
        const token = await getToken();
        const [c, d] = await Promise.all([
          getArchiveContainer(token, containerId),
          listArchiveDatasets(token, containerId),
        ]);
        setContainer(c);
        setDatasets(d.items);
        const first = d.items[0];
        if (first) setActiveId(first.dataset_id);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      }
    })();
  }, [containerId, getToken]);

  useEffect(() => {
    if (!activeId) return;
    void (async () => {
      const token = await getToken();
      setSchema(await getArchiveDatasetSchema(token, activeId));
    })();
  }, [activeId, getToken]);

  const loadViews = useCallback(async () => {
    try {
      const token = await getToken();
      setViews((await listRecordViews(token, containerId)).items);
    } catch {
      // A container with no views is the common case, not an error worth
      // showing — the tables beside it still work.
      setViews([]);
    }
  }, [getToken, containerId]);

  useEffect(() => {
    void loadViews();
  }, [loadViews]);

  useEffect(() => {
    if (!activeViewId) {
      setViewRows(null);
      return;
    }
    void (async () => {
      try {
        const token = await getToken();
        setViewRows(await getRecordViewRows(token, activeViewId, filters, freeText, page, 50));
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      }
    })();
  }, [activeViewId, getToken, filters, freeText, page]);

  async function removeView(viewId: string) {
    try {
      const token = await getToken();
      await deleteRecordView(token, containerId, viewId);
      if (activeViewId === viewId) setActiveViewId(null);
      notifications.show({
        title: "Record view deleted",
        message: "The archived tables are untouched — a view is only a saved way of reading them.",
        color: "teal",
      });
      await loadViews();
    } catch (err) {
      notifications.show({
        title: "Could not delete the view",
        message: err instanceof Error ? err.message : String(err),
        color: "red",
      });
    }
  }

  const loadRows = useCallback(async () => {
    if (!activeId) return;
    const token = await getToken();
    setRows(await getArchiveDatasetRows(token, activeId, filters, freeText, page, 50));
  }, [activeId, getToken, filters, freeText, page]);

  useEffect(() => {
    void loadRows();
  }, [loadRows]);

  async function doExport() {
    if (!activeId || !schema) return;
    setBusy(true);
    try {
      const token = await getToken();
      await exportArchiveDataset(token, activeId, filters, freeText, `${schema.dataset_name}.csv`);
      notifications.show({
        title: "Exported",
        message: "The audit trail records the filter — exactly which slice left the archive.",
        color: "teal",
      });
    } catch (err) {
      notifications.show({
        title: "Export failed",
        message: err instanceof Error ? err.message : String(err),
        color: "red",
      });
    } finally {
      setBusy(false);
    }
  }

  async function showManifest() {
    if (!activeId) return;
    openManifest();
    const token = await getToken();
    setManifest(await getArchiveDatasetManifest(token, activeId));
  }

  if (error) {
    return (
      <Alert color="orange" variant="light" title="No access to this archive">
        {error}
      </Alert>
    );
  }

  const columns = schema?.columns ?? [];
  const active = datasets.find((d) => d.dataset_id === activeId);

  return (
    <Stack gap="lg">
      <div>
        <Title order={3}>{container?.name ?? "Retired system archive"}</Title>
        <Text size="sm" c="dimmed" mt={4}>
          {container?.description}
        </Text>
      </div>

      {container && (
        <Alert
          icon={<IconPlugConnectedX size={18} />}
          color="violet"
          variant="light"
          title={`${container.source_system} — decommissioned, data preserved`}
        >
          <Group gap="xl">
            <Text size="sm">
              Decommissioned <strong>{formatDate(container.decommissioned_on)}</strong>
            </Text>
            <Text size="sm">
              <strong>{formatCount(container.record_count)}</strong> rows preserved across{" "}
              <strong>{datasets.length}</strong> tables
            </Text>
            <Text size="sm">
              Retention <strong>{container.retention_class}</strong>
            </Text>
          </Group>
        </Alert>
      )}

      <Group align="flex-start" gap="lg" wrap="nowrap">
        <Stack gap="xs" w={240} style={{ flexShrink: 0 }}>
          <Text size="xs" fw={700} tt="uppercase" c="dimmed">
            Tables
          </Text>
          {datasets.map((dataset) => (
            <Card
              key={dataset.dataset_id}
              withBorder
              padding="sm"
              radius="md"
              style={{
                cursor: "pointer",
                borderColor:
                  dataset.dataset_id === activeId ? "var(--mantine-color-violet-6)" : undefined,
              }}
              onClick={() => {
                setActiveId(dataset.dataset_id);
                setActiveViewId(null);
                setFilters([]);
                setFreeText("");
                setPage(1);
              }}
            >
              <Group gap={8} wrap="nowrap">
                <IconTable size={16} color="var(--mantine-color-violet-4)" />
                <div style={{ minWidth: 0 }}>
                  <Text size="sm" fw={500}>
                    {dataset.dataset_name}
                  </Text>
                  <Text size="xs" c="dimmed">
                    {formatCount(dataset.row_count)} rows · {dataset.column_count} cols
                  </Text>
                </div>
              </Group>
            </Card>
          ))}

          {views.length > 0 && (
            <>
              <Text size="xs" fw={700} tt="uppercase" c="dimmed" mt="sm">
                Record views
              </Text>
              <Text size="xs" c="dimmed" mt={-6}>
                One row per record, across tables.
              </Text>
              {views.map((view) => (
                <Card
                  key={view.view_id}
                  withBorder
                  padding="sm"
                  radius="md"
                  style={{
                    cursor: "pointer",
                    borderColor:
                      view.view_id === activeViewId ? "var(--mantine-color-teal-6)" : undefined,
                  }}
                  onClick={() => {
                    setActiveViewId(view.view_id);
                    setFilters([]);
                    setFreeText("");
                    setPage(1);
                  }}
                >
                  <Group gap={8} wrap="nowrap" justify="space-between">
                    <Group gap={8} wrap="nowrap" style={{ minWidth: 0 }}>
                      <IconBinaryTree2 size={16} color="var(--mantine-color-teal-5)" />
                      <div style={{ minWidth: 0 }}>
                        <Text size="sm" fw={500} lineClamp={1}>
                          {view.name}
                        </Text>
                        <Text size="xs" c="dimmed" lineClamp={1}>
                          {view.description || "Joined record"}
                        </Text>
                      </div>
                    </Group>
                    {canSaveView && (
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        size="sm"
                        aria-label="Delete this record view"
                        onClick={(event) => {
                          event.stopPropagation();
                          void removeView(view.view_id);
                        }}
                      >
                        <IconTrash size={14} />
                      </ActionIcon>
                    )}
                  </Group>
                </Card>
              ))}
            </>
          )}
        </Stack>

        <Stack gap="md" style={{ flex: 1, minWidth: 0 }}>
          {activeViewId && viewRows && (
            <Paper withBorder radius="md">
              <Group justify="space-between" p="sm">
                <div>
                  <Text fw={600}>
                    {views.find((v) => v.view_id === activeViewId)?.name ?? "Record view"}
                  </Text>
                  <Text size="xs" c="dimmed">
                    {formatCount(viewRows.total)} records · joined across tables
                  </Text>
                </div>
              </Group>
              <ScrollArea>
                <Table striped fz="xs" verticalSpacing={5}>
                  <Table.Thead>
                    <Table.Tr>
                      {viewRows.columns.map((c) => (
                        <Table.Th key={String(c.key)}>{String(c.label ?? c.key)}</Table.Th>
                      ))}
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {viewRows.rows.map((row, i) => (
                      <Table.Tr key={i}>
                        {viewRows.columns.map((c) => {
                          const value = row[String(c.key)];
                          const empty = value === null || value === undefined || value === "";
                          return (
                            <Table.Td key={String(c.key)}>
                              {empty ? (
                                // Named rather than blank: an empty cell in a left
                                // join means "no matching row", which is a fact
                                // about the archive, not a rendering gap.
                                <Text size="xs" c="dimmed" fs="italic">
                                  no match
                                </Text>
                              ) : (
                                String(value)
                              )}
                            </Table.Td>
                          );
                        })}
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </ScrollArea>
              {viewRows.total > viewRows.page_size && (
                <Group justify="center" p="sm">
                  <Pagination
                    size="sm"
                    value={page}
                    total={Math.ceil(viewRows.total / viewRows.page_size)}
                    onChange={setPage}
                  />
                </Group>
              )}
            </Paper>
          )}

          {!activeViewId && active && (
            <Paper withBorder p="md" radius="md">
              <Group justify="space-between" mb={schemaOpen ? "sm" : 0}>
                <Group gap="xs">
                  <ActionIcon variant="subtle" size="sm" onClick={toggleSchema}>
                    <IconChevronDown
                      size={16}
                      style={{
                        transform: schemaOpen ? undefined : "rotate(-90deg)",
                        transition: "transform 150ms",
                      }}
                    />
                  </ActionIcon>
                  <Text fw={600}>{active.dataset_name}</Text>
                  <Text size="sm" c="dimmed">
                    {active.description}
                  </Text>
                </Group>
                <Group gap="xs">
                  <Badge variant="default" tt="none">
                    {formatBytes(active.size_bytes)}
                  </Badge>
                  {datasets.length > 1 && (
                    <Button
                      size="compact-sm"
                      variant="light"
                      leftSection={<IconLink size={14} />}
                      onClick={openJoin}
                    >
                      Join with another table
                    </Button>
                  )}
                  <Button
                    size="compact-sm"
                    variant="light"
                    leftSection={<IconFileCertificate size={14} />}
                    onClick={showManifest}
                  >
                    Manifest
                  </Button>
                </Group>
              </Group>

              <Collapse in={schemaOpen}>
                <ScrollArea>
                  <Table verticalSpacing={5} striped fz="xs">
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Column</Table.Th>
                        <Table.Th>Type</Table.Th>
                        <Table.Th>Nulls</Table.Th>
                        <Table.Th>Distinct</Table.Th>
                        <Table.Th>Sample values</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {columns.map((column) => {
                        const col = column as Record<string, unknown>;
                        return (
                          <Table.Tr key={String(col.name)}>
                            <Table.Td>
                              <Text size="xs" fw={500}>
                                {String(col.name)}
                              </Text>
                            </Table.Td>
                            <Table.Td>
                              <Badge size="xs" variant="default" tt="none">
                                {String(col.type)}
                              </Badge>
                            </Table.Td>
                            <Table.Td>
                              <Text size="xs" c="dimmed">
                                {formatCount(Number(col.null_count ?? 0))}
                              </Text>
                            </Table.Td>
                            <Table.Td>
                              <Text size="xs" c="dimmed">
                                {formatCount(Number(col.distinct_count ?? 0))}
                              </Text>
                            </Table.Td>
                            <Table.Td>
                              <Text size="xs" c="dimmed" lineClamp={1}>
                                {((col.samples as string[]) ?? []).join(" · ")}
                              </Text>
                            </Table.Td>
                          </Table.Tr>
                        );
                      })}
                    </Table.Tbody>
                  </Table>
                </ScrollArea>
              </Collapse>
            </Paper>
          )}

          <Paper withBorder p="md" radius="md">
            <Group gap="xs" wrap="nowrap" align="flex-end">
              <Select
                label="Column"
                placeholder="Pick a column"
                size="xs"
                w={170}
                searchable
                value={draft.column}
                data={columns.map((c) => String((c as Record<string, unknown>).name))}
                onChange={(value) => setDraft((d) => ({ ...d, column: value ?? "" }))}
              />
              <Select
                label="Condition"
                size="xs"
                w={130}
                value={draft.op}
                data={OPS}
                onChange={(value) => setDraft((d) => ({ ...d, op: value ?? "eq" }))}
              />
              <TextInput
                label="Value"
                size="xs"
                w={160}
                value={draft.value}
                onChange={(e) => setDraft((d) => ({ ...d, value: e.currentTarget.value }))}
              />
              <Button
                size="xs"
                disabled={!draft.column || !draft.value}
                onClick={() => {
                  setFilters((prev) => [...prev, draft]);
                  setDraft({ column: "", op: "eq", value: "" });
                  setPage(1);
                }}
              >
                Add filter
              </Button>
              <TextInput
                label="Search all columns"
                size="xs"
                flex={1}
                leftSection={<IconSearch size={14} />}
                value={freeText}
                onChange={(e) => {
                  setFreeText(e.currentTarget.value);
                  setPage(1);
                }}
              />
              <Button
                size="xs"
                variant="light"
                leftSection={<IconDatabaseExport size={14} />}
                loading={busy}
                onClick={doExport}
              >
                Export CSV
              </Button>
            </Group>

            {(filters.length > 0 || freeText) && (
              <Group gap={6} mt="sm">
                {filters.map((filter, index) => (
                  <Badge
                    key={index}
                    variant="light"
                    color="violet"
                    tt="none"
                    style={{ cursor: "pointer" }}
                    onClick={() => setFilters((prev) => prev.filter((_, i) => i !== index))}
                  >
                    {filter.column} {OPS.find((o) => o.value === filter.op)?.label} {filter.value} ✕
                  </Badge>
                ))}
                <Button
                  size="compact-xs"
                  variant="subtle"
                  leftSection={<IconFilterOff size={12} />}
                  onClick={() => {
                    setFilters([]);
                    setFreeText("");
                    setPage(1);
                  }}
                >
                  Clear
                </Button>
              </Group>
            )}
          </Paper>

          <Group justify="space-between">
            <Text size="sm" c="dimmed">
              {rows
                ? `${formatCount(rows.total)} of ${formatCount(active?.row_count ?? 0)} rows`
                : "…"}
            </Text>
            {rows && rows.total > rows.page_size && (
              <Pagination
                size="sm"
                value={page}
                onChange={setPage}
                total={Math.ceil(rows.total / rows.page_size)}
              />
            )}
          </Group>

          {!rows && (
            <Group justify="center" py="xl">
              <Loader size="sm" />
            </Group>
          )}

          {rows?.rows.length === 0 && (
            <Paper withBorder p="xl" radius="md">
              <Text ta="center" fw={500}>
                No rows match this filter
              </Text>
              <Text ta="center" size="sm" c="dimmed" mt={4}>
                Remove a condition to widen the query.
              </Text>
            </Paper>
          )}

          {!!rows?.rows.length && (
            <Paper withBorder radius="md">
              <ScrollArea>
                <Table striped highlightOnHover verticalSpacing={6} fz="xs">
                  <Table.Thead>
                    <Table.Tr>
                      {columns.map((c) => (
                        <Table.Th key={String((c as Record<string, unknown>).name)}>
                          {String((c as Record<string, unknown>).name)}
                        </Table.Th>
                      ))}
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {rows.rows.map((row, index) => (
                      <Table.Tr key={index}>
                        {columns.map((c) => {
                          const name = String((c as Record<string, unknown>).name);
                          return (
                            <Table.Td key={name} style={{ whiteSpace: "nowrap" }}>
                              {String((row as Record<string, unknown>)[name] ?? "")}
                            </Table.Td>
                          );
                        })}
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </ScrollArea>
            </Paper>
          )}
        </Stack>
      </Group>

      {activeId && (
        <ArchiveJoinBuilder
          opened={joinOpen}
          onClose={closeJoin}
          containerId={containerId}
          datasets={datasets}
          baseDatasetId={activeId}
          canSave={canSaveView}
          onSaved={() => void loadRows()}
        />
      )}

      <Modal opened={manifestOpen} onClose={closeManifest} title="Extraction manifest" size="lg">
        <Text size="sm" c="dimmed" mb="sm">
          How completeness is proven for a system that no longer exists.
        </Text>
        {!manifest && <Loader size="sm" />}
        {manifest && (
          <Table verticalSpacing={5}>
            <Table.Tbody>
              {Object.entries(manifest)
                .filter(([key]) => key !== "columns")
                .map(([key, value]) => (
                  <Table.Tr key={key}>
                    <Table.Td w={180}>
                      <Text size="xs" c="dimmed">
                        {key}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" ff={key === "sha256" ? "monospace" : undefined}>
                        {String(value)}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
            </Table.Tbody>
          </Table>
        )}
      </Modal>
    </Stack>
  );
}
